import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  PlusIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { trpc } from "../../lib/api/trpc";

type ApiToken = {
  id: string;
  name: string;
  description: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  isExpired: boolean;
};

function RevealTokenModal({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setAcknowledged(true);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-surface rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-semibold text-text-base mb-2">
            Your API key has been created
          </h3>
          <p className="text-sm text-text-muted mb-4">
            Copy this key now — it will not be shown again.
          </p>

          <div className="bg-surface-2 rounded-lg p-4 mb-4">
            <code className="text-sm font-mono text-text-base break-all">
              {token}
            </code>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong transition-colors"
            >
              {copied ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy to clipboard
                </>
              )}
            </button>
          </div>

          {!copied && (
            <label className="flex items-center gap-2 text-sm text-text-muted mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="rounded border-subtle text-accent"
              />
              I have saved my key
            </label>
          )}

          <button
            onClick={onClose}
            disabled={!acknowledged}
            className="w-full px-4 py-2 text-sm font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateTokenModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (token: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [expiryOption, setExpiryOption] = useState("never");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useContext();

  const getExpiryDate = (): string | undefined => {
    const now = new Date();
    switch (expiryOption) {
      case "30d":
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
      case "90d":
        return new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
      case "1y":
        return new Date(
          now.getTime() + 365 * 24 * 60 * 60 * 1000,
        ).toISOString();
      default:
        return undefined;
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    setError(null);

    try {
      // @ts-ignore - tRPC type inference
      const result = await utils.client.apiTokens.create.mutate({
        name: name.trim(),
        description: description.trim() || undefined,
        expiresAt: getExpiryDate(),
      });
      // @ts-ignore
      await utils.apiTokens.list.invalidate();
      onCreated(result.plainTextToken);
    } catch (err: any) {
      setError(err?.message || "Failed to create token");
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-surface rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-base">
              Create API Key
            </h3>
            <button
              onClick={onClose}
              className="text-text-faint hover:text-text-muted"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. CI/CD Pipeline"
                className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-text-base text-sm focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this key is used for"
                className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-text-base text-sm focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Expiry
              </label>
              <select
                value={expiryOption}
                onChange={(e) => setExpiryOption(e.target.value)}
                className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-text-base text-sm"
              >
                <option value="never">Never expires</option>
                <option value="30d">30 days</option>
                <option value="90d">90 days</option>
                <option value="1y">1 year</option>
              </select>
            </div>

            {error && <p className="text-sm text-err">{error}</p>}

            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!name.trim() || isCreating}
                className="px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreating ? "Creating..." : "Create key"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ApiKeysPage() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [revealToken, setRevealToken] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const utils = trpc.useContext();

  // @ts-ignore - tRPC type inference
  const { data, isLoading } = trpc.apiTokens.list.useQuery();
  const tokens = (data as ApiToken[]) || [];

  const handleRevoke = useCallback(
    async (id: string) => {
      try {
        // @ts-ignore
        await utils.client.apiTokens.revoke.mutate({ id });
        // @ts-ignore
        await utils.apiTokens.list.invalidate();
        setConfirmRevokeId(null);
      } catch {
        // silent
      }
    },
    [utils],
  );

  const handleCreated = (token: string) => {
    setCreateModalOpen(false);
    setRevealToken(token);
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-text-base">API Keys</h2>
          <p className="mt-1 text-sm text-text-muted">
            Use API keys to authenticate programmatic access to the document
            ingest API.{" "}
            <Link
              to="/settings/api-docs"
              className="text-accent hover:underline"
            >
              View API documentation
            </Link>
          </p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Create API key
        </button>
      </div>

      {/* Security banner */}
      <div className="bg-warn-bg border border-warn rounded-lg p-4 mb-6 flex items-start gap-3">
        <ShieldExclamationIcon className="h-5 w-5 text-warn flex-shrink-0 mt-0.5" />
        <p className="text-sm text-warn">
          API keys grant access to document ingestion for your organisation.
          Treat them like passwords — do not commit them to source control or
          share them publicly. Revoke any key that may have been compromised.
        </p>
      </div>

      {/* Token list */}
      {isLoading ? (
        <div className="bg-surface rounded-lg shadow-box p-8 animate-pulse">
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-12 bg-surface-2 rounded" />
            ))}
          </div>
        </div>
      ) : tokens.length === 0 ? (
        <div className="bg-surface rounded-lg shadow-box p-8 text-center">
          <p className="text-sm text-text-faint">
            No API keys yet. Create one to enable programmatic document
            ingestion.
          </p>
        </div>
      ) : (
        <div className="bg-surface rounded-lg shadow-box overflow-hidden">
          <table className="min-w-full divide-y divide-[color:var(--border)]">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-faint uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-faint uppercase">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-faint uppercase">
                  Last Used
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-faint uppercase">
                  Expires
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-faint uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-faint uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {tokens.map((token) => (
                <tr key={token.id}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-text-base">
                      {token.name}
                    </div>
                    {token.description && (
                      <div className="text-xs text-text-faint">
                        {token.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-muted">
                    {formatDistanceToNow(new Date(token.createdAt), {
                      addSuffix: true,
                    })}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-muted">
                    {token.lastUsedAt
                      ? formatDistanceToNow(new Date(token.lastUsedAt), {
                          addSuffix: true,
                        })
                      : "Never"}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-muted">
                    {token.expiresAt
                      ? formatDistanceToNow(new Date(token.expiresAt), {
                          addSuffix: true,
                        })
                      : "Never"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        token.isExpired
                          ? "bg-err-bg text-err"
                          : "bg-ok-bg text-ok"
                      }`}
                    >
                      {token.isExpired ? "Expired" : "Active"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {confirmRevokeId === token.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleRevoke(token.id)}
                          className="text-xs text-err font-medium hover:underline"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmRevokeId(null)}
                          className="text-xs text-text-faint hover:text-text-muted"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRevokeId(token.id)}
                        className="text-xs text-err hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {createModalOpen && (
        <CreateTokenModal
          onClose={() => setCreateModalOpen(false)}
          onCreated={handleCreated}
        />
      )}

      {/* One-time reveal modal */}
      {revealToken && (
        <RevealTokenModal
          token={revealToken}
          onClose={() => setRevealToken(null)}
        />
      )}
    </div>
  );
}
